import { useCallback, useEffect, useMemo, useState } from 'react'

import { Tabs } from '@loadsmart/loadsmart-ui'

import { featureFlag } from 'core/config/feature-flags/constants.featureFlags'
import { RenderFeature } from 'core/config/feature-flags/RenderFeature'
import { RequestStatus } from 'core/domain/Request'
import { formatMode } from 'core/presenters/modePresenter'

import { useConfigContext } from 'config/ui/ConfigContext'

import { type AccessorialRate } from '../domain/Accessorial'
import { getLanesPerModePreference } from '../domain/Lane'
import { type Mode } from '../domain/Mode'
import { type Proposal } from '../domain/Proposal'
import { RfpTabOptions } from '../domain/RfpTabs'
import { useFetchAccessorialRates } from './hooks/useFetchAccessorialRates'
import { RfpAccessorialList } from './RfpAccessorialList'
import { RfpLaneList } from './RfpLaneList'

export type RfpTabsProps = {
  rfpId: string
  proposal: Proposal
  isBidDisabled: boolean
  displayResult: boolean
  onTabChange?: (tab: string) => void
  onHasFilledAccessorialRatesChange: (hasFilledAccessorialRates: boolean) => void
}

export function RfpTabs({
  rfpId,
  proposal,
  isBidDisabled,
  displayResult,
  onTabChange,
  onHasFilledAccessorialRatesChange,
}: RfpTabsProps) {
  const configs = useConfigContext()
  const enablePriceItemTypes = configs.enablePriceItemTypes.toBoolean()

  const [activeTab, setActiveTab] = useState<string>(RfpTabOptions.Lanes)

  const { accessorialRates, fetchAccessorialRatesStatus, fetchAccessorialRates } =
    useFetchAccessorialRates()

  const [filledRateFields, setFilledRateFields] = useState<Set<number | string>>(new Set())

  const lanesPerMode = useMemo(() => getLanesPerModePreference(proposal.lanes), [proposal.lanes])

  useEffect(() => {
    const controller = new AbortController()

    fetchAccessorialRates(rfpId, controller)

    return () => {
      controller.abort()
    }
  }, [rfpId, fetchAccessorialRates])

  useEffect(() => {
    if (enablePriceItemTypes) {
      const typesIds = accessorialRates.map(rate => rate.priceItemType?.uuid!)
      setFilledRateFields(new Set(typesIds))
    } else {
      const types = accessorialRates.map(rate => rate.type!)
      setFilledRateFields(new Set(types))
    }
  }, [accessorialRates, enablePriceItemTypes])

  useEffect(() => {
    const hasFilledAccessorialRates = enablePriceItemTypes
      ? filledRateFields.size === proposal.requestedAccessorials.length
      : filledRateFields.size === proposal.accessorials.length

    onHasFilledAccessorialRatesChange(hasFilledAccessorialRates)
  }, [
    enablePriceItemTypes,
    filledRateFields,
    proposal.requestedAccessorials.length,
    proposal.accessorials.length,
    onHasFilledAccessorialRatesChange,
  ])

  useEffect(() => {
    setActiveTab(Object.keys(lanesPerMode)[0])
  }, [lanesPerMode])

  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      if (onTabChange) {
        onTabChange(tab)
      }
    },
    [onTabChange]
  )

  const onRfpRowBlur = useCallback(
    (rate: AccessorialRate) => {
      const newSet = new Set(filledRateFields)
      const isInvalidRate = Number(rate.rate) === 0
      const isEmptyNotes = !rate.notes
      const rateFieldId = enablePriceItemTypes ? rate.priceItemType?.uuid! : rate.type!

      if (isInvalidRate && isEmptyNotes) {
        newSet.delete(rateFieldId)
        setFilledRateFields(newSet)
      } else {
        setFilledRateFields(newSet.add(rateFieldId))
      }
    },
    [filledRateFields, enablePriceItemTypes]
  )

  return (
    <Tabs direction="horizontal" activeTab={activeTab} onTabChange={handleTabChange}>
      <Tabs.Items>
        {Object.keys(lanesPerMode).map(mode => (
          <Tabs.Item name={mode} key={`rfp-tab-lane-${mode}`}>
            {`Lanes ${formatMode(mode as Mode)}`}&nbsp; &bull; &nbsp;
            {lanesPerMode[mode as Mode].length}
          </Tabs.Item>
        ))}
        <RenderFeature flag={featureFlag.rfpAccessorials}>
          <Tabs.Item name={RfpTabOptions.Accessorials}>Accessorials</Tabs.Item>
        </RenderFeature>
      </Tabs.Items>
      <Tabs.Panels>
        {Object.keys(lanesPerMode).map(mode => (
          <Tabs.Panel name={mode} key={`rfp-tabpanel-lane-${mode}`}>
            <RfpLaneList
              rfpId={rfpId}
              currency={proposal.rfp.currency}
              lanes={lanesPerMode[mode as Mode]}
              preferenceMode={mode as Mode}
              isBidDisabled={isBidDisabled}
              shipperCompanyName={proposal.shipperCompanyName}
              displayResult={displayResult && proposal.rfp.enableCarrierFeedback}
              distanceUnit={proposal.rfp.distanceUnit}
            />
          </Tabs.Panel>
        ))}
        <RenderFeature flag={featureFlag.rfpAccessorials}>
          <Tabs.Panel name={RfpTabOptions.Accessorials} style={{ overflowX: 'auto' }}>
            {fetchAccessorialRatesStatus === RequestStatus.Done && (
              <RfpAccessorialList
                rfpId={rfpId}
                currency={proposal.rfp.currency}
                accessorials={proposal.accessorials}
                requestedAccessorials={proposal.requestedAccessorials}
                accessorialRates={accessorialRates}
                onRowBlur={onRfpRowBlur}
                disabled={Boolean(proposal.submittedAt)}
              />
            )}
          </Tabs.Panel>
        </RenderFeature>
      </Tabs.Panels>
    </Tabs>
  )
}
